/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import * as Sentry from '@sentry/browser'
import { isEmpty } from 'lodash'
import { KeyboardEvent, FocusEvent } from 'react'
import { parse, format, isMatch } from 'date-fns'
import { th, enUS } from 'date-fns/locale'
import i18n from 'config/i18n'

import { DATE, STATUS_OPTIONS, WARRANTY } from 'helpers/constants'
import type { IIndexable } from 'helpers/model'
import type { IParam } from 'libs/apis'
import {
  Address,
  AddressOptionKey,
  Contact,
  CONTACT_TYPE,
  Field,
  OptionProps,
  STATUS_READABLE,
  ILoaderOption,
} from 'types'
import type { IParamForSubmit } from 'libs/apis/helper'
import {
  ICountry,
  IDistrict,
  IProvince,
  ISubDistrict,
} from 'features/Address/model'
import { version } from '../../package.json'

import type { IUploadPhoto } from 'features/Ticket'

import { WARRANTY_TYPE } from 'types'

const locale = i18n.language === 'th' ? th : enUS

export const currentVersion: string = version

export enum LoadingStatus {
  Pending,
  Success,
  Failure,
}

export type queryParamProps = {
  q: string
  _page: number
  _ids: number[]
  _sort: 'ASC' | 'DESC'
  _order: string
  isActive: string
}

export const addSearchParams = (
  urlSearchParams: IParamForSubmit,
  searchParams: IParamForSubmit,
): IIndexable & IParam => {
  const nextSearchParams = {
    ...urlSearchParams,
    ...searchParams,
  } as IIndexable & IParam

  for (const [key, value] of Object.entries(searchParams)) {
    if (!value) {
      delete nextSearchParams[key]
    }
  }

  return nextSearchParams
}

export const findSelected = (
  options?: OptionProps[],
  key?: unknown,
): OptionProps | null => {
  if (!options) {
    return null
  }

  return options.find((item: OptionProps) => item.key === key) || null
}

export const parseDate = (date?: string): Date | null => {
  if (!date || !isMatch(date, DATE.QUERY)) {
    return null
  }

  try {
    const parsed = parse(date, DATE.QUERY, new Date())

    return parsed
  } catch (error) {
    Sentry.captureException(error)

    return null
  }
}

export const formatDate = (date?: Date | null): string => {
  if (!date) {
    return ''
  }

  try {
    const formatted = format(date, DATE.QUERY)

    return formatted
  } catch (error) {
    Sentry.captureException(error)

    return ''
  }
}

export const numberStart = (
  limit: string | number,
  page: string | number,
): number => {
  return +limit * (+page - 1) + 1
}

export const statusFilter = (status: boolean): STATUS_READABLE =>
  status ? i18n.t('common:active.label') : i18n.t('common:inactive.label')

export const getPhoneNumber = (contacts: Contact[]): string => {
  if (isEmpty(contacts)) {
    return '-'
  }

  const phoneContact = contacts.find(
    (contact) => contact.type === CONTACT_TYPE.PHONE,
  )

  if (!phoneContact) {
    return '-'
  }

  const nextPhoneNumber = `${phoneContact.number}`

  return nextPhoneNumber
}

export const addressOptionPropsFormatter = <T>(
  data: T,
  field: Field,
): OptionProps<AddressOptionKey>[] => {
  if (Array.isArray(data)) {
    const nextOption: OptionProps<AddressOptionKey>[] = data.map(
      ({ id, name, native }) => {
        const key = {
          id,
          [`${field}Th`]: native,
          [`${field}En`]: name,
        }

        return {
          key,
          label: native,
        }
      },
    )

    return nextOption
  }

  return []
}

export const addressOptionFormatter = <
  T extends { id: string; [label: string]: string },
>(
  data: T,
  field: Field,
): OptionProps<AddressOptionKey> | undefined => {
  if (data.id) {
    return {
      key: data,
      label: data[`${field}Th`],
    }
  }
}

export const onEnter =
  (callback: (e: KeyboardEvent<HTMLInputElement>) => void) =>
  (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      callback(e)
    }
  }

export const thaiCountryCodeOption = { key: '+66', label: '+66' }

export const getPhoneNumberMaxLength = (value: string): number =>
  value.startsWith('0') ? 10 : 9

export const getPhoneFormatter = (input: string) => {
  // Strip all characters from the input except digits
  input = input.replace(/\D/g, '')

  // Trim the remaining input to ten characters, to preserve phone number format
  input = input.substring(0, 10)

  const size = input.length
  if (size == 0 || size < 4) {
    input = input
  } else if (size == 10) {
    input = input.substring(0, 3) + '-' + input.substring(3, 10)
  } else if (size == 9) {
    input = input.substring(0, 2) + '-' + input.substring(2, 9)
  }

  return input
}

export const formatPhoneNumberOnBlur =
  (callback: (value: string) => void) =>
  (e: FocusEvent<HTMLInputElement>): void => {
    if (!e.target.value.startsWith('0') && e.target.value) {
      callback(getPhoneFormatter(`0${e.target.value}`))
    } else {
      callback(getPhoneFormatter(`${e.target.value}`))
    }
  }

export const validateKeyPress =
  <T>(pattern: RegExp) =>
  (e: KeyboardEvent<T>): void => {
    if (!pattern.test(e.key)) {
      e.preventDefault()
    }
  }

export const getAddressField = (
  fieldDataList: ICountry[] | IProvince[] | ISubDistrict[] | IDistrict[],
  field: Field,
  address: Address & IIndexable,
): AddressOptionKey => {
  const fieldData = fieldDataList.find(
    (_fieldData) => _fieldData.native === address[`${field}Th`],
  )

  return {
    id: fieldData?.id || '',
    [`${field}Th`]: address[`${field}Th`] || '',
    [`${field}En`]: address[`${field}En`] || '',
  }
}

// call from API old version
export const getImage = (path?: string): string | undefined => {
  if (path) {
    return `${window._env_.REACT_APP_API_URL}/v1/image/${path}`
  }
}

// call from API new version
export const getImageFileName = (fileName?: string): string | undefined => {
  if (fileName) {
    return `${window._env_.REACT_APP_API_URL}/v1/image/imageUploads/${fileName}`
  }
}

export const removeSessionStorage = (key: string): void => {
  sessionStorage.removeItem(key)
}

export const getSessionStorage = (key: string): string | null => {
  return sessionStorage.getItem(key)
}

export const setSessionStorage = (key: string, value: string): void => {
  sessionStorage.setItem(key, value)
}

export function stringEnumToArray(eObj: {
  [k: string]: string | number
}): Array<string | number> {
  return Object.keys(eObj).map((key: string) => eObj[key])
}

export const convertStatus = (
  status: string | boolean | undefined,
): OptionProps =>
  STATUS_OPTIONS.find(({ key }) => key === status) || STATUS_OPTIONS[1]

export const onPageChange = (setPage: () => void): void => {
  setPage()
  window.scrollTo(0, 0)
}

export const setSelectedOption = (
  key: string,
  label: string,
): ILoaderOption | undefined => {
  return {
    key,
    label,
    value: key,
  }
}

export const resizeImage = (
  file: File,
  maxWidth: number,
  maxHeight: number,
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const image = new Image()

    image.src = URL.createObjectURL(file)
    image.onload = () => {
      const width = image.width
      const height = image.height

      if (width <= maxWidth && height <= maxHeight) {
        resolve(file)
      }

      const scale = Math.min(maxWidth / width, maxHeight / height)

      const newWidth = width * scale
      const newHeight = height * scale

      const canvas = document.createElement('canvas')

      canvas.width = newWidth
      canvas.height = newHeight

      const context = canvas.getContext('2d')

      context?.drawImage(image, 0, 0, newWidth, newHeight)

      canvas.toBlob(resolve, file.type)
    }

    image.onerror = reject
  })
}

export const blobToFile = (blob: Blob, fileName: string) => {
  const newBlob: any = blob

  newBlob.lastModifiedDate = new Date()
  newBlob.name = fileName

  return newBlob
}

export const setValue = (value?: string | null): string =>
  value ? `${value}` : '-'

export const setFormatDate = (date: Date | null) => {
  let formatTime = '-'

  if (i18n.language === 'th' && date) {
    formatTime = format(new Date(date), 'dd MMM yyyy, p', { locale })
  }

  if (i18n.language === 'en' && date) {
    formatTime = format(new Date(date), 'dd MMM yyyy, h:mm a', { locale })
  }

  return formatTime
}

export const setFormatAssignDate = (date: Date | null): string => {
  return date ? format(new Date(date), 'dd MMM yyyy', { locale }) : '-'
}

export const setFormatTime = (date: Date | null): string => {
  let formatTime = '-'

  if (i18n.language === 'th' && date) {
    formatTime = format(new Date(date), 'p', { locale })
  }

  if (i18n.language === 'en' && date) {
    formatTime = format(new Date(date), 'h:mm a', { locale })
  }

  return formatTime
}

export const getValueByLanguage = (
  nameTh?: string | null,
  nameEn?: string | null,
): string => {
  switch (i18n.language) {
    case 'th':
      return setValue(nameTh)

    case 'en':
      return setValue(nameEn)

    default:
      return setValue(nameTh)
  }
}

export const setListPreviewImages = (
  list: IUploadPhoto[] | null,
): { src: string | undefined }[] => {
  return list
    ? list.map((item) => {
        return {
          src: getImageFileName(item.photo),
        }
      })
    : []
}

export const getPriceFormatter = (value: string) => {
  if (value.indexOf('.') > 0) {
    const integerValue = value
      .slice(0, value.indexOf('.'))
      .replace(/\D/g, '')
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    const decimalValue = value.slice(value.indexOf('.'), value.length)

    return `${integerValue}${decimalValue}`
  }

  return value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const setPriceFormatter = (
  price?: number | null,
  currency?: string | null,
): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'THB',
  })

  return formatter.format(price || 0)
}

export const setPriceDigitsFormatter = (
  price?: number | null,
  currency?: string | null,
): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'THB',
  })

  const currencyData = currency || 'THB'
  const data = formatter.format(price || 0)

  return data.slice(currencyData.length + 1, data.length)
}

export const setMaintenancePeriodFormat = (
  period?: number | null,
  unit?: string | null,
): string => {
  if (!unit && !period) {
    return '-'
  }

  switch (unit?.toLowerCase()) {
    case 'day':
      return `${period} ${i18n.t('product:warranty.unit.day')}`

    case 'month':
      return `${period} ${i18n.t('product:warranty.unit.month')}`

    case 'year':
      return `${period} ${i18n.t('product:warranty.unit.year')}`

    default:
      return '-'
  }
}

export const trimFirstSpace = (
  event:
    | React.ChangeEvent<HTMLInputElement>
    | React.ChangeEvent<HTMLTextAreaElement>,
) => {
  return event.target.value.replace(/^\s+|\p{Emoji_Presentation}/gu, '')
}

export const handleDisableOnPaste = (
  e: React.ClipboardEvent<HTMLInputElement>,
) => {
  e.preventDefault()
}

export const generateSkeletonList = (
  rowOdd: Record<string, React.ReactNode>,
  rowEven: Record<string, React.ReactNode>,
  loopOptional = 30,
): Record<string, React.ReactNode>[] => {
  const list = []

  for (let i = 1; i <= loopOptional; i++) {
    if (i % 2 === 0) {
      list.push(rowEven)
    } else {
      list.push(rowOdd)
    }
  }

  return list
}

export const copyToClipboard = async (text: string) => {
  if (text) {
    const textArea = document.createElement('textarea')

    textArea.value = text
    textArea.select()
    textArea.style.display = 'none'

    try {
      navigator.clipboard.writeText(textArea.value)
    } catch (err) {
      // TODO: update later when the requirement is ready
    }
  }
}

export const getWarrantyType = (
  startWarranty?: string | null,
  endWarranty?: string | null,
  warrantyType?: WARRANTY_TYPE | string,
): string => {
  const now = new Date().getTime()

  let start = undefined
  let end = undefined
  let data: string = WARRANTY.NO_WARRANTY

  if (warrantyType?.toLowerCase() === WARRANTY_TYPE.LIFETIME) {
    data = WARRANTY.ACTIVE
  }

  if (startWarranty && endWarranty) {
    start = new Date(startWarranty).getTime()
    end = new Date(endWarranty).getTime()

    if (now > end) {
      data = WARRANTY.EXPIRES
    }

    if (start < now && now < end) {
      data = WARRANTY.ACTIVE
    }
  }

  return data
}

export const getQueryParams = (path: string): queryParamProps => {
  const urlParams = new URLSearchParams(path)
  const whitelist = ['q', '_page', '_ids', '_sort', '_order', 'isActive']

  const queryParams: any[] = []

  whitelist.forEach((item) => {
    queryParams.push(urlParams.get(item))
  })

  const [q, page, ids, sort, order, isActive] = queryParams
  const newIds =
    typeof ids === 'string' && ids ? ids.split(',').map((id) => Number(id)) : []

  return {
    q: q || '',
    _page: Number(page) || 1,
    _ids: newIds,
    _sort: sort || '',
    _order: order || 'ASC',
    isActive: isActive || '',
  }
}

export const isWindow = Boolean(
  window.navigator.appVersion.indexOf('Win') !== -1,
)
